import React, { useState, useEffect } from 'react';
import API_URL from '../config/api';
import './AdminPanel.css';

export default function AdminPanel({ onClose }) {
  const [activeTab, setActiveTab] = useState('stats');
  const [stats, setStats] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchStats = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/api/admin/stats`);
      const data = await response.json();
      if (data.success) {
        setStats(data.stats);
      } else {
        setError('Failed to fetch statistics');
      }
    } catch (err) {
      setError('Cannot connect to server. Make sure backend is running on port 5000.');
    } finally {
      setLoading(false);
    }
  };

  const fetchInquiries = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/api/admin/inquiries`);
      const data = await response.json();
      if (data.success) {
        setInquiries(data.data);
      } else {
        setError('Failed to fetch inquiries');
      }
    } catch (err) {
      setError('Cannot connect to server. Make sure backend is running on port 5000.');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/api/admin/users`);
      const data = await response.json();
      if (data.success) {
        setUsers(data.data);
      } else {
        setError('Failed to fetch users');
      }
    } catch (err) {
      setError('Cannot connect to server. Make sure backend is running on port 5000.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'stats') {
      fetchStats();
    } else if (activeTab === 'inquiries') {
      fetchInquiries();
    } else if (activeTab === 'users') {
      fetchUsers();
    }
  }, [activeTab]);

  const deleteInquiry = async (id) => {
    if (!window.confirm('Are you sure you want to delete this inquiry?')) {
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/admin/inquiry/${id}`, {
        method: 'DELETE',
      });
      const data = await response.json();
      if (data.success) {
        fetchInquiries(); // Refresh list
      }
    } catch (err) {
      setError('Failed to delete inquiry');
    }
  };

  return (
    <div className="admin-overlay">
      <div className="admin-container">
        <button className="admin-close" onClick={onClose}>×</button>
        <div className="admin-header">
          <h1>🔐 Admin Panel</h1>
          <p>View database entries and user inputs</p>
        </div>

        {error && (
          <div className="admin-error">
            {error}
            {error.includes('Cannot connect') && (
              <div style={{marginTop: '0.5rem', fontSize: '0.9rem'}}>
                <strong>To fix:</strong> Run <code>cd backend && npm run dev</code> in terminal
              </div>
            )}
          </div>
        )}

        <div className="admin-tabs">
          <button
            className={activeTab === 'stats' ? 'admin-tab active' : 'admin-tab'}
            onClick={() => setActiveTab('stats')}
          >
            📊 Statistics
          </button>
          <button
            className={activeTab === 'inquiries' ? 'admin-tab active' : 'admin-tab'}
            onClick={() => setActiveTab('inquiries')}
          >
            📧 Inquiries ({inquiries.length})
          </button>
          <button
            className={activeTab === 'users' ? 'admin-tab active' : 'admin-tab'}
            onClick={() => setActiveTab('users')}
          >
            👥 Users ({users.length})
          </button>
        </div>

        <div className="admin-content">
          {loading && <div className="admin-loading">Loading...</div>}

          {activeTab === 'stats' && stats && (
            <div className="stats-grid">
              <div className="stat-card">
                <h3>Total Users</h3>
                <p className="stat-number">{stats.totalUsers}</p>
              </div>
              <div className="stat-card">
                <h3>Total Inquiries</h3>
                <p className="stat-number">{stats.totalInquiries}</p>
              </div>
              <div className="stat-card full-width">
                <h3>Recent Inquiries</h3>
                {stats.recentInquiries.length > 0 ? (
                  <div className="recent-list">
                    {stats.recentInquiries.map((inq) => (
                      <div key={inq._id} className="recent-item">
                        <div>
                          <strong>{inq.name}</strong> ({inq.email})
                          <p style={{marginTop: '0.25rem', color: '#666', fontSize: '0.9rem'}}>
                            {inq.message.substring(0, 100)}...
                          </p>
                        </div>
                        <span style={{color: '#999', fontSize: '0.85rem'}}>
                          {new Date(inq.created).toLocaleDateString()}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p>No inquiries yet</p>
                )}
              </div>
              <div className="stat-card full-width">
                <h3>Recent Users</h3>
                {stats.recentUsers.length > 0 ? (
                  <div className="recent-list">
                    {stats.recentUsers.map((user) => (
                      <div key={user._id} className="recent-item">
                        <div>
                          <strong>{user.name}</strong>
                          <p style={{marginTop: '0.25rem', color: '#666', fontSize: '0.9rem'}}>
                            {user.email}
                          </p>
                        </div>
                        <span style={{color: '#999', fontSize: '0.85rem'}}>
                          {new Date(user.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p>No users yet</p>
                )}
              </div>
            </div>
          )}

          {activeTab === 'inquiries' && (
            <div className="data-table">
              {inquiries.length > 0 ? (
                <>
                  <button onClick={fetchInquiries} className="refresh-btn">🔄 Refresh</button>
                  <table>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Message</th>
                        <th>Date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {inquiries.map((inq) => (
                        <tr key={inq._id}>
                          <td>{inq.name}</td>
                          <td>{inq.email}</td>
                          <td className="message-cell">{inq.message}</td>
                          <td>{new Date(inq.created).toLocaleDateString()}</td>
                          <td>
                            <button
                              className="delete-btn"
                              onClick={() => deleteInquiry(inq._id)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              ) : (
                <div className="empty-state">
                  <p>No inquiries found. Submit a contact form to see data here!</p>
                  <button onClick={fetchInquiries} className="refresh-btn">🔄 Refresh</button>
                </div>
              )}
            </div>
          )}

          {activeTab === 'users' && (
            <div className="data-table">
              {users.length > 0 ? (
                <>
                  <button onClick={fetchUsers} className="refresh-btn">🔄 Refresh</button>
                  <table>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Joined</th>
                        <th>User ID</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user) => (
                        <tr key={user._id}>
                          <td>{user.name}</td>
                          <td>{user.email}</td>
                          <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                          <td className="id-cell">{user._id}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              ) : (
                <div className="empty-state">
                  <p>No users found. Sign up to see data here!</p>
                  <button onClick={fetchUsers} className="refresh-btn">🔄 Refresh</button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}




